import { useState, useRef, useEffect, useMemo, FormEvent } from 'react';
import { useChat } from '@/hooks/use-chat';
import { useFileAttachments } from '@/hooks/use-file-attachments';
import { useAgent } from '@/hooks/use-agents';
import { getHostTools, getHostName, type OfficeToolDefinition } from '@/tools/office-tools-registry';
import { isVisionModel, VISION_REQUIRED_TOOLS } from '@/utils/vision-models';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MarkdownRenderer } from '@/components/ui/markdown-renderer';
import { cn } from '@/utils/cn';
import {
  FileAttachmentButton,
  AttachmentChipList,
  DropZone,
  MessageAttachmentIndicator,
} from '@/components/ui/file-attachment-area';
import { ToolCallGroup } from '@/components/ui/tool-call-group';
import { QuestionSheet } from '@/components/ui/question-sheet';
// Connectors are managed in agent config (AgentDialog), not during chat sessions
import {
  Send,
  Square,
  AlertCircle,
  Wrench,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Archive,
  Lightbulb,
  Loader2,
} from 'lucide-react';
import { ChatState } from '@/hooks/use-chat';
import type { ChatMessage } from '@/types/chat.types';
import { FollowUpSuggestions, extractFollowUpSuggestions, stripSystemReminder } from '@/components/chat/follow-up-suggestions';

interface ChatInterfaceProps {
  selectedAgentId?: string;
  initialSessionId?: string | null;
  workspaceId?: string | null;
  onRegisterCancel?: (cancelFn: () => void | Promise<void>) => void;
  onSessionCreated?: (sessionId: string) => void;
  onChatStateChange?: (isActive: boolean) => void;
  agentRefreshKey?: number;
}

export function ChatInterface({ selectedAgentId, initialSessionId, workspaceId, onRegisterCancel, onSessionCreated, onChatStateChange, agentRefreshKey }: ChatInterfaceProps = {}) {
  const [inputValue, setInputValue] = useState('');
  const [hideSuggestions, setHideSuggestions] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const officeHost = getHostName();
  // Load only the tools for the detected Office host (async dynamic import).
  // Retries up to 5 times if Office.onReady hasn't fired yet.
  const [tools, setTools] = useState<OfficeToolDefinition[]>([]);
  const [toolRetry, setToolRetry] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    getHostTools().then(hostTools => {
      if (cancelled) return;
      setTools(hostTools);
      // If we got very few tools, Office.onReady may not have fired yet — retry
      if (hostTools.length <= 3 && toolRetry < 5) {
        retryTimer = setTimeout(() => setToolRetry(r => r + 1), 1000);
      }
    });
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [officeHost, toolRetry]);

  const { agent, loadAgent } = useAgent(workspaceId || null, selectedAgentId || null);

  // Filter out vision-dependent tools when the agent's model doesn't support images
  const filteredTools = useMemo(() => {
    if (!tools.length) return tools;
    if (!agent || isVisionModel(agent.providerTemplate?.slug, agent.providerModel?.slug)) {
      return tools;
    }
    return tools.filter((t) => !VISION_REQUIRED_TOOLS.has(t.name));
  }, [tools, agent]);

  const {
    messages,
    isLoading,
    chatState,
    error,
    sessionId,
    isArchived,
    activeSkills,
    pendingQuestion,
    sendMessage,
    cancelRequest,
    handleQuestionComplete,
    handleQuestionSkip,
    handleQuestionDismiss,
    approveToolCall,
    rejectToolCall,
  } = useChat({
    sessionId: initialSessionId || undefined,
    agentId: selectedAgentId,
    workspaceId: workspaceId || undefined,
    officeHost,
    tools: filteredTools,
    onSessionCreated,
    onChatStateChange,
  });

  const {
    pending: pendingAttachments,
    addFiles,
    removeFile,
    getAttachmentMetadata,
    getRawFiles,
    clearAll: clearAttachments,
  } = useFileAttachments();
  const quickPrompts = useMemo(() => {
    const values = agent?.quickStartPrompts ? Object.values(agent.quickStartPrompts) : [];
    return values.map((value) => (value || '').trim()).filter(Boolean);
  }, [agent?.quickStartPrompts]);

  const isEmptyChat = messages.length === 0;
  const isSessionLoading = chatState === ChatState.RECONNECTING;
  const showTypingIndicator = isLoading && !isSessionLoading;

  // Register cancel function with parent so back button can stop the chat
  useEffect(() => {
    onRegisterCancel?.(cancelRequest);
  }, [onRegisterCancel, cancelRequest]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (!selectedAgentId || !workspaceId) return;
    loadAgent();
  }, [agentRefreshKey, selectedAgentId, workspaceId, loadAgent]);

  // Reset suggestions visibility when a new assistant message arrives
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.role === 'assistant' && !isLoading) {
      setHideSuggestions(false);
    }
  }, [messages, isLoading]);

  // Auto-resize textarea as user types
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = '28px'; // Reset to min height
    const scrollHeight = textarea.scrollHeight;
    const maxHeight = 96; // 4 lines
    textarea.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
  }, [inputValue]);

  // Focus input on mount, new chat, or session switch
  useEffect(() => {
    if (isArchived) return;
    const textarea = textareaRef.current;
    if (!textarea) return;
    const focusTimer = window.requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);
    });
    return () => window.cancelAnimationFrame(focusTimer);
  }, [sessionId, isArchived]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const message = inputValue;
    const attachments = getAttachmentMetadata();
    const files = getRawFiles();
    setInputValue('');
    setHideSuggestions(true); // Hide suggestions when user sends message
    if (attachments.length > 0) clearAttachments();
    await sendMessage(
      message,
      files.length > 0 ? files : undefined,
      attachments.length > 0 ? attachments : undefined,
    );
  };

  const handleSuggestionClick = async (suggestion: string) => {
    if (isLoading) return;
    setInputValue('');
    setHideSuggestions(true); // Hide suggestions immediately when clicked
    await sendMessage(suggestion);
  };

  // Group consecutive tool calls together for compact display
  const renderMessages = (msgs: ChatMessage[]) => {
    const elements: React.ReactNode[] = [];
    let toolGroup: ChatMessage[] = [];

    // Find last assistant message index for follow-up suggestions
    let lastAssistantMsgIndex = -1;
    for (let i = msgs.length - 1; i >= 0; i -= 1) {
      if (msgs[i].role === 'assistant' && !msgs[i].toolCallId) {
        lastAssistantMsgIndex = i;
        break;
      }
    }

    for (let i = 0; i < msgs.length; i++) {
      const msg = msgs[i];
      const isToolCall = msg.toolCallId && msg.toolName;
      const isLastAssistantMessage = i === lastAssistantMsgIndex;

      if (isToolCall) {
        toolGroup.push(msg);
      } else {
        // Not a tool call - flush any pending tool group
        if (toolGroup.length > 0) {
          if (toolGroup.length > 1) {
            elements.push(
              <ToolCallGroup key={`group-${toolGroup[0].id}`} messages={toolGroup} onApprove={approveToolCall} onReject={rejectToolCall} />
            );
          } else {
            elements.push(<MessageBubble key={toolGroup[0].id} message={toolGroup[0]} onApprove={approveToolCall} onReject={rejectToolCall} />);
          }
          toolGroup = [];
        }
        // Render the non-tool message
        elements.push(
          <MessageBubble 
            key={msg.id} 
            message={msg} 
            isLastAssistantMessage={isLastAssistantMessage}
            onSuggestionClick={handleSuggestionClick}
            isLoading={isLoading}
            hideSuggestions={hideSuggestions}
          />
        );
      }
    }

    // Flush any remaining tool group
    if (toolGroup.length > 0) {
      if (toolGroup.length > 1) {
        elements.push(
          <ToolCallGroup key={`group-${toolGroup[0].id}`} messages={toolGroup} onApprove={approveToolCall} onReject={rejectToolCall} />
        );
      } else {
        elements.push(<MessageBubble key={toolGroup[0].id} message={toolGroup[0]} onApprove={approveToolCall} onReject={rejectToolCall} />);
      }
    }

    return elements;
  };

  if (isSessionLoading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3">
        <Loader2 className="size-6 text-primary animate-spin" />
        <p className="text-sm text-muted-foreground">Loading conversation…</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex min-h-0 relative overflow-hidden">
      {/* Main Chat Area */}
      <DropZone onFiles={addFiles}>
        <div className="flex-1 flex flex-col min-h-0 min-w-0 relative z-10">
          {/* Error banner */}
          {error && (
            <div className="px-3 py-2 bg-destructive/10 border-b border-destructive/20 flex items-center gap-2 animate-fade-in-up">
              <AlertCircle className="size-4 text-destructive shrink-0" />
              <p className="text-xs text-destructive flex-1">{typeof error === 'string' ? error : JSON.stringify(error)}</p>
            </div>
          )}

          {/* Active skills banner */}
          {activeSkills.length > 0 && (
            <div className="px-3 py-1.5 bg-primary/5 border-b border-primary/10 flex items-center gap-2 animate-fade-in-up">
              <Lightbulb className="size-3.5 text-primary shrink-0" />
              <p className="text-[11px] text-primary flex-1 truncate">
                <span className="font-medium">Skills:</span>{' '}
                {activeSkills.map(s => s.name).join(', ')}
              </p>
            </div>
          )}

        <div className={cn('flex-1 min-h-0', isEmptyChat ? 'relative' : 'flex flex-col')}>
          {/* Empty state - render outside ScrollArea when empty */}
          {isEmptyChat && <EmptyState officeHost={officeHost} toolCount={tools.length} />}
          
          {/* Messages area */}
          <ScrollArea className={cn(isEmptyChat ? 'absolute inset-0 pointer-events-none' : 'flex-1 min-h-0')}>
            <div className="w-full px-2 py-3 space-y-2 min-w-0 overflow-x-hidden">
              {!isEmptyChat && renderMessages(messages)}
              {showTypingIndicator && <TypingIndicator />}
              <div ref={scrollRef} />
            </div>
          </ScrollArea>

          {/* Input area or archived banner */}
          {isArchived ? (
            <div className={cn(isEmptyChat ? 'absolute inset-x-0 bottom-0' : 'shrink-0') + ' border-t border-border bg-background'}>
              <div className="flex gap-3 p-3 bg-muted/50">
                <Archive className="size-6 text-muted-foreground" />
                <p className="text-xs text-muted-foreground leading-relaxed max-w-[320px]">
                  This chat is archived. You can view history but cannot send new messages.
                </p>
              </div>
            </div>
          ) : (
            <div
              className={cn(
                isEmptyChat
                  ? 'absolute inset-x-0 bottom-0 pb-2 transition-all duration-300 ease-out'
                  : 'shrink-0'
              )}
            >
              <div className="px-3 pb-1 max-w-md mx-auto w-full">
                {/* Hint text + quick prompts (only on empty chat) */}
                {isEmptyChat && !isArchived && (
                  <div className="pb-3 animate-fade-in-up space-y-2">
                    {quickPrompts.length === 0 && (
                      <p className="text-xs text-muted-foreground/70 text-center">
                        Start typing to begin a conversation
                      </p>
                    )}
                    {quickPrompts.length > 0 && !hideSuggestions && (
                      <div className="max-w-md mx-auto w-full">
                        <div className="rounded-lg border bg-card/70 px-3 py-2">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-[11px] font-medium text-muted-foreground">Quick prompts</p>
                            <span className="text-[10px] text-muted-foreground">{quickPrompts.length}</span>
                          </div>
                          <div className="flex flex-col gap-2">
                            {quickPrompts.map((prompt, index) => (
                              <button
                                key={`${prompt}-${index}`}
                                type="button"
                                onClick={() => handleSuggestionClick(prompt)}
                                title={prompt}
                                className={cn(
                                  'group relative overflow-hidden text-left p-2 rounded-lg border border-border bg-accent/30 transition-all duration-200',
                                  'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
                                  'hover:bg-accent/50 hover:border-foreground/10'
                                )}
                              >
                                <p className="text-xs text-foreground line-clamp-2">
                                  {prompt}
                                </p>
                                <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 bg-gradient-to-r from-primary/5 to-transparent transition-opacity duration-200" />
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* Chat input */}
                <form onSubmit={handleSubmit} className="p-1">
                  <div className="input-border-runner rounded-lg bg-background/90 backdrop-blur-sm px-2 py-1 shadow-md border border-border/60 cursor-text relative overflow-hidden">
                    {/* Gradient accent on focus */}
                    <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                    <div className="relative z-10">
                      <AttachmentChipList
                        attachments={pendingAttachments}
                        onRemove={removeFile}
                      />
                      <div className="flex items-center gap-1.5 rounded-md">
                        <FileAttachmentButton
                          onFiles={addFiles}
                          disabled={isLoading}
                        />
                        <textarea
                          ref={textareaRef}
                          autoFocus
                          value={inputValue}
                          onChange={(e) => setInputValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSubmit(e);
                            }
                          }}
                          placeholder="Type a message..."
                          aria-label="Message input"
                          disabled={isLoading}
                          rows={1}
                          className="flex-1 text-xs bg-transparent border-0 px-0 py-0.5 resize-none overflow-y-auto focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 placeholder:text-muted-foreground"
                          style={{ minHeight: '22px', maxHeight: '88px', lineHeight: '1.35rem' }}
                        />
                        {isLoading ? (
                          <Button
                            type="button"
                            onClick={cancelRequest}
                            size="icon"
                            variant="destructive"
                            className="shrink-0 size-8"
                            title="Stop generating"
                          >
                            <Square className="size-3" />
                          </Button>
                        ) : (
                          <Button
                            type="submit"
                            disabled={!inputValue.trim()}
                            size="icon"
                            className="shrink-0 size-8"
                          >
                            <Send className="size-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Session info intentionally hidden for cleaner UI */}
                </form>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Ask-user-question overlay */}
      {pendingQuestion && (
        <QuestionSheet
          questions={pendingQuestion.questions}
          onComplete={handleQuestionComplete}
          onSkip={handleQuestionSkip}
          onDismiss={handleQuestionDismiss}
        />
      )}
    </DropZone>
    </div>
  );
}
// ─── Empty State ─────────────────────────────

function EmptyState({ officeHost, toolCount }: { officeHost: string; toolCount: number }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center px-4 text-center -mt-12">
      <div className="relative">
        {/* Background glow effect */}
        <div className="absolute -inset-16 bg-gradient-radial from-primary/10 via-primary/5 to-transparent blur-3xl opacity-60" />
        
        {/* Icon and greeting */}
        <div className="relative flex items-center justify-center gap-2 mb-3 animate-fade-in-up">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-primary/30 blur-xl animate-pulse" style={{ animationDuration: '3s' }} />
            <div className="relative size-10 rounded-xl bg-gradient-to-br from-primary/20 via-primary/10 to-primary/5 border border-primary/30 flex items-center justify-center shadow-lg backdrop-blur-sm">
              <Sparkles strokeWidth={1.5} className="size-5 text-primary relative z-10" />
            </div>
          </div>
          <h1 className="text-base font-semibold text-foreground flex items-center gap-1.5">
            Hello!
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-primary/10 text-primary leading-none">
              Beta
            </span>
          </h1>
        </div>
        
        {/* Subheading */}
        <p className="relative text-sm font-normal text-foreground/70 mb-4 animate-fade-in-up max-w-[180px] mx-auto leading-relaxed" style={{ animationDelay: '0.1s' }}>
          How can I help you with {officeHost} today?
        </p>

        {/* Tool count badge */}
      </div>
    </div>
  );
}

// ─── Typing Indicator ────────────────────────

function TypingIndicator() {
  return (
    <div className="flex justify-start pl-3 animate-fade-in-up" role="status" aria-live="polite">
      <span className="text-xs font-semibold shimmer-text-neutral">
        Thinking…
      </span>
    </div>
  );
}

// ─── Message Bubble ──────────────────────────

function MessageBubble({ message, onApprove, onReject, isLastAssistantMessage, onSuggestionClick, isLoading: isChatLoading, hideSuggestions }: {
  message: ChatMessage;
  onApprove?: (toolCallId: string) => void;
  onReject?: (toolCallId: string, feedback?: string) => void;
  isLastAssistantMessage?: boolean;
  onSuggestionClick?: (suggestion: string) => void;
  isLoading?: boolean;
  hideSuggestions?: boolean;
}) {
  const isUser = message.role === 'user';
  const isTool = message.role === 'tool';
  const isSystem = message.role === 'system';
  const isAssistant = message.role === 'assistant';
  const rawContent = message.content == null
    ? ''
    : typeof message.content === 'string'
      ? message.content
      : JSON.stringify(message.content);
  const sanitizedContent = stripSystemReminder(rawContent);
  
  // Parse suggestions from content
  const { cleanContent, suggestions } = typeof message.content === 'string'
    ? extractFollowUpSuggestions(sanitizedContent)
    : { cleanContent: sanitizedContent, suggestions: [] };
    
  const displayContent = cleanContent.trim() ? cleanContent : sanitizedContent;
  if (displayContent.trim() === '/exit') {
    return null;
  }

  // Tool call messages
  if (message.toolCallId && message.toolName) {
    return <ToolCallBubble message={message} onApprove={onApprove} onReject={onReject} />;
  }

  // Hide tool result / system messages (they're shown in tool call status)
  if (isTool || isSystem) {
    return null;
  }

  if (!displayContent.trim() && !isUser) {
    return null;
  }

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} ${isUser ? 'animate-slide-in-right' : 'animate-slide-in-left'}`}>
      <div className={isUser ? 'max-w-[75%]' : 'max-w-[95%] w-full'}>
        <div
          className={`rounded-lg px-3 py-2 text-xs sm:text-sm text-left break-words [overflow-wrap:anywhere] min-w-0 ${
            isUser
              ? 'bg-primary text-white w-fit'
              : 'text-foreground w-full max-w-full overflow-x-hidden'
          }`}
        >
          {isUser ? (
            <p className="whitespace-pre-wrap leading-relaxed break-words" style={{ overflowWrap: 'anywhere' }}>{displayContent}</p>
          ) : (
            <MarkdownRenderer content={displayContent} />
          )}

          {isUser && message.attachments && message.attachments.length > 0 && (
            <MessageAttachmentIndicator attachments={message.attachments} />
          )}
        </div>
        
        {/* Follow-up suggestions for last assistant message */}
        {isAssistant && isLastAssistantMessage && onSuggestionClick && !hideSuggestions && (
          <FollowUpSuggestions
            suggestions={suggestions}
            disabled={isChatLoading}
            onSelect={onSuggestionClick}
          />
        )}
      </div>
    </div>
  );
}

// ─── Tool Call Bubble ────────────────────────
// Matches the HAI assistants UI: collapsible accordion with streaming status,
// animated icons, structured input/output display, and log-tree border.

function ToolCallBubble({ message, onApprove, onReject }: {
  message: ChatMessage;
  onApprove?: (toolCallId: string) => void;
  onReject?: (toolCallId: string, feedback?: string) => void;
}) {
  const hasExpandableContent = !!(message.toolArgs || message.toolResult != null || message.toolError);

  // Closed by default - user can open manually if they want to see details
  const [isOpen, setIsOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const isConnectorTool = message.toolName?.includes('__');
  const isMcpTool = message.toolName?.includes('.') || isConnectorTool;
  const displayName = isConnectorTool
    ? message.toolName!.split('__').pop()
    : message.toolName?.includes('.')
      ? message.toolName.split('.').pop()
      : message.toolName;
  const sourceLabel = isConnectorTool
    ? message.toolName!.split('__')[0]
    : message.toolName?.includes('.')
      ? message.toolName.split('.')[0]
      : null;

  const isAwaitingApproval = message.toolStatus === 'awaiting_approval';
  const isComplete = message.toolStatus === 'success';
  const isError = message.toolStatus === 'error';
  const isPending = message.toolStatus === 'pending';

  // Dynamic title based on state (matches HAI pattern)
  // Use message.content if available (contains custom display names), otherwise use displayName
  const title = isAwaitingApproval
    ? `Requesting to use ${displayName}`
    : isPending || isComplete
      ? message.content || (isMcpTool ? 'Using MCP tool' : `Using ${displayName}`)
      : isError
        ? `${displayName} failed`
        : displayName;

  // Border color based on state
  const borderClass = isError
    ? 'tool-call-border-error'
    : isComplete
      ? 'tool-call-border-success'
      : 'tool-call-border';

  return (
    <div className="animate-fade-in-up min-w-0 overflow-hidden">
      <div className={cn(borderClass, 'min-w-0 overflow-hidden')}>
        {/* Header / trigger */}
        <button
          type="button"
          onClick={() => hasExpandableContent && setIsOpen(!isOpen)}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          className="flex items-center gap-1.5 w-full text-left group py-0.5 min-w-0"
        >
          {/* Status icon */}
          <ToolStatusIcon status={message.toolStatus} />

          {/* Title with shimmer when processing */}
          <span className={cn(
            'text-xs font-medium text-muted-foreground truncate min-w-0',
            isPending && 'shimmer-text',
          )}>
            {title}
          </span>

          {/* Badges */}
          {isAwaitingApproval && (
            <span className="text-[10px] text-amber-600 bg-amber-500/10 px-1.5 py-0.5 rounded font-medium shrink-0">
              approval required
            </span>
          )}

          {isMcpTool && sourceLabel && (
            <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0">
              {sourceLabel}
            </span>
          )}

          <div className="flex-1" />

          {/* Expand chevron */}
          {hasExpandableContent && isOpen ? (
            <ChevronDown className="size-3 text-muted-foreground shrink-0" />
          ) : hasExpandableContent && isHovering ? (
            <ChevronRight className="size-3 text-muted-foreground shrink-0" />
          ) : null}
        </button>

        {/* Collapsible content — log-style tree view */}
        {isOpen && hasExpandableContent && (
          <div className="mt-1 tool-call-border-muted tool-collapsible-enter min-w-0 overflow-hidden">
            <div className="space-y-1.5 py-1 min-w-0">
              {/* Input params */}
              {message.toolArgs && Object.keys(message.toolArgs).length > 0 && (
                <div className="max-h-52 overflow-y-auto overflow-x-hidden custom-scrollbar">
                  <div className="font-mono text-[11px] break-all">
                    {isMcpTool && (
                      <div className="mb-0.5">
                        <span className="text-muted-foreground">toolName:</span>{' '}
                        <span className="text-foreground/80">{displayName}</span>
                      </div>
                    )}
                    {Object.entries(message.toolArgs).map(([key, value]) => (
                      <div key={key} className="mb-0.5">
                        <span className="text-muted-foreground">{key}:</span>{' '}
                        <span className="text-foreground/80 break-all">{formatValue(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Error */}
              {message.toolError && (
                <div className="text-[11px] font-mono text-destructive/80 bg-destructive/5 rounded px-2 py-1 border border-destructive/10 overflow-hidden">
                  <pre className="whitespace-pre-wrap break-all">{message.toolError}</pre>
                </div>
              )}

              {/* Output result */}
              {message.toolResult != null && (
                <div className="max-h-52 overflow-y-auto overflow-x-hidden custom-scrollbar">
                  <pre className="whitespace-pre-wrap break-all font-mono text-[11px] text-foreground/70">{
                    typeof message.toolResult === 'string'
                      ? truncateStr(message.toolResult, 500)
                      : truncateStr(JSON.stringify(message.toolResult, null, 2), 500)
                  }</pre>
                </div>
              )}

              {/* Processing indicator */}
              {isPending && !message.toolResult && !message.toolError && (
                <div className="text-[11px] text-muted-foreground shimmer-text">
                  Processing tool call...
                </div>
              )}
            </div>
          </div>
        )}

        {/* Approval buttons */}
        {isAwaitingApproval && message.toolCallId && (
          <div className="mt-1.5 flex items-center gap-2">
            <Button
              size="sm"
              variant="default"
              className="h-6 text-[11px] px-2.5"
              onClick={(e) => { e.stopPropagation(); onApprove?.(message.toolCallId!); }}
            >
              <CheckCircle2 className="size-3 mr-1" />
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-6 text-[11px] px-2.5"
              onClick={(e) => { e.stopPropagation(); onReject?.(message.toolCallId!); }}
            >
              <XCircle className="size-3 mr-1" />
              Reject
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Status icon matching HAI assistants pattern */
function ToolStatusIcon({ status }: { status?: ChatMessage['toolStatus'] }) {
  switch (status) {
    case 'awaiting_approval':
      return <AlertCircle className="size-3.5 text-amber-500 shrink-0" />;
    case 'success':
      return <CheckCircle2 className="size-3.5 text-success shrink-0" />;
    case 'error':
      return <XCircle className="size-3.5 text-destructive shrink-0" />;
    case 'pending':
    default:
      return <Wrench className="size-3.5 text-primary shrink-0 animate-wrench" />;
  }
}

/** Format a single value for key:value display */
function formatValue(value: unknown): string {
  if (typeof value === 'string') {
    return value.length > 500 ? value.slice(0, 500) + '...' : value;
  }
  const str = JSON.stringify(value);
  return str.length > 500 ? str.slice(0, 500) + '...' : str;
}

/** Truncate long strings */
function truncateStr(str: string, maxLen = 1200): string {
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen) + `\n... (${str.length - maxLen} more chars)`;
}
